import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faPenToSquare, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getMergeFields } from 'api/engage';
import * as DeleteMergeField from 'assets/js/pages/engage/components/Modals/DeleteMergeField';
import * as MergeFieldForm from 'assets/js/pages/engage/components/Modals/MergeFieldForm';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { accountQueryKeys } from 'queries/account-query-keys';
import { MergeField } from 'types/domain';

const columnHelper = createColumnHelper<Omit<MergeField, 'id'> & { id?: number }>();

const MergeFields = () => {
    const { accountId } = useCurrentAccount();

    const { pagination, setPagination } = Table.useReactTableState();
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.mergeFieldsWithParams(accountId, params),
        queryFn: () => getMergeFields(accountId, params),
        select: data => {
            const defaultItems = data.defaults.map(defaultItem => ({
                name: defaultItem.name,
                title: defaultItem.title,
                value: '',
                ...defaultItem.resource,
            }));
            const mergeFields = data.data.filter(
                item => !defaultItems.find(defaultItem => defaultItem.name === item.name),
            );

            return {
                ...data,
                data: [...(params.page === 1 ? defaultItems : []), ...mergeFields],
            };
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Merge field',
                cell: info => {
                    const item = info.row.original;
                    return (
                        <div className="max-w-48 overflow-hidden text-ellipsis whitespace-nowrap">
                            {item.title}
                        </div>
                    );
                },
            }),
            columnHelper.display({
                header: 'Tag',
                cell: info => {
                    const item = info.row.original;
                    return (
                        <div className="max-w-56 overflow-hidden text-ellipsis whitespace-nowrap">
                            {`${data?.prefix}${item.name}${data?.suffix}`}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('value', {
                header: 'Default value',
                cell: info => {
                    const item = info.row.original;
                    return item.value ? (
                        <div className="max-w-40 overflow-hidden text-ellipsis whitespace-nowrap">
                            {item.value}
                        </div>
                    ) : (
                        <span className="text-gray-400">No default value</span>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const item = info.row.original;

                    return (
                        <DropdownMenu.Root key={item.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content>
                                <MergeFieldForm.Root item={item}>
                                    <MergeFieldForm.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            <div className="flex gap-2">
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                Edit
                                            </div>
                                        </DropdownMenu.Item>
                                    </MergeFieldForm.Trigger>
                                </MergeFieldForm.Root>
                                {item.id && (
                                    <DeleteMergeField.Root itemId={item.id}>
                                        <DeleteMergeField.Trigger asChild>
                                            <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                                <div className="flex gap-2 text-red-600">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                    Delete
                                                </div>
                                            </DropdownMenu.Item>
                                        </DeleteMergeField.Trigger>
                                    </DeleteMergeField.Root>
                                )}
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [data?.prefix, data?.suffix],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
            <div className={'flex flex-col gap-4'}>
                <Box className="flex flex-col overflow-hidden">
                    <div className="p-6">
                        <h2 className="text-lg font-medium">Merge fields</h2>
                        <div className="flex items-center justify-center gap-5">
                            <p className="text-sm text-gray-600">
                                Merge fields are variables that can match a contact’s details. When
                                a field has no data, a default value can be added to avoid blank
                                spaces in your content.
                            </p>
                            <MergeFieldForm.Root>
                                <MergeFieldForm.Trigger asChild>
                                    <Button variant="outline" color="neutral">
                                        Add new default
                                    </Button>
                                </MergeFieldForm.Trigger>
                            </MergeFieldForm.Root>
                        </div>
                    </div>
                    <Separator />
                    <div className="overflow-x-auto">
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No merge fields found</Table.EmptyTitle>
                        </Table.Empty>
                    </div>
                </Box>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </div>
        </Table.Root>
    );
};

export { MergeFields };
